import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { RING_STATUS } from "score-common";
import { useScoreApi } from "../api/scoreApi";
import { classNumberLabel } from "../util/classDisplay";

// #1936 — the Set half of the Ring section (docs/score/ring-state.md). A
// class is REQUIRED: PUT /ring-state 400s with "running_requires_class" for
// a classId-less RUNNING (score-api's setRingState handler), so the submit
// button stays disabled without one and that 400 is normally unreachable —
// still handled below (curated message) for the race where it isn't, per
// AttachRideDialog.jsx's `err?.response?.data?.code` idiom (common-fe's
// error interceptor rewrites err.message but preserves this field). Ride is
// optional: a ring can be set to a class before any of its rides has opened
// (AC17 in the #1934 groundwork).
export default function SetRingStateDialog({
  open,
  ring,
  showId,
  classes,
  ridesByClassId,
  onClose,
  onSaved,
}) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();

  const [classId, setClassId] = useState("");
  const [rideId, setRideId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setClassId("");
      setRideId("");
      setError(null);
    }
  }, [open]);

  const ringClasses = useMemo(
    () => (classes || []).filter((c) => c.ring === ring),
    [classes, ring]
  );

  const ridesForClass = classId ? ridesByClassId[classId] || [] : [];

  const handleSubmit = async () => {
    if (!classId) return;
    setSaving(true);
    setError(null);
    try {
      const ride = rideId ? ridesForClass.find((r) => r.id === rideId) : null;
      await scoreApi.ringState.setRingState({
        showId,
        ring,
        status: RING_STATUS.RUNNING,
        classId,
        rideId: rideId || null,
        scheduledTime: ride?.scheduledTime ?? null,
      });
      onSaved?.();
    } catch (err) {
      const code = err?.response?.data?.code;
      setError(
        code === "running_requires_class"
          ? t("ringState.errors.runningRequiresClass")
          : err?.serverErrorDetail || err?.message || t("ringState.errors.generic")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="set-ring-state-dialog">
      <DialogTitle>{t("ringState.setDialog.title", { ring })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <FormControl fullWidth required>
            <InputLabel id="ring-state-class-label">
              {t("ringState.setDialog.classLabel")}
            </InputLabel>
            <Select
              labelId="ring-state-class-label"
              label={t("ringState.setDialog.classLabel")}
              value={classId}
              data-testid="ring-state-class-select"
              onChange={(e) => {
                setClassId(e.target.value);
                setRideId("");
              }}
            >
              {ringClasses.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {classNumberLabel(c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={!classId}>
            <InputLabel id="ring-state-ride-label">
              {t("ringState.setDialog.rideLabel")}
            </InputLabel>
            <Select
              labelId="ring-state-ride-label"
              label={t("ringState.setDialog.rideLabel")}
              value={rideId}
              data-testid="ring-state-ride-select"
              onChange={(e) => setRideId(e.target.value)}
            >
              <MenuItem value="">{t("ringState.setDialog.noRide")}</MenuItem>
              {ridesForClass.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.riderName || r.horseName || t("common.unknownUser")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("ringState.setDialog.cancel")}</Button>
        <Button
          variant="contained"
          disabled={!classId || saving}
          onClick={handleSubmit}
          data-testid="ring-state-set-confirm"
        >
          {saving ? <CircularProgress size={20} /> : t("ringState.setDialog.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
